import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import AddVoiture from '../components/AddVoiture';
import VoitureCard from '../components/VoitureCard';
import VoitureGalleryModal from '../components/VoitureGallery'; // Changed import name
import Sidebar from '../components/Sidebar';
import CategoryManager from '../components/CategoryManager';
import QuickTransactionCreator from '../components/QuickTransactionCreator';


// SVG Icons
const CarIcon = () => (
    <svg className="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
    </svg>
);

const AddIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
);

const CloseIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
);

const FilterIcon = () => (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
    </svg>
);

const LoadingSpinner = () => (
    <div className="flex justify-center items-center h-64">
        <div className="relative">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
            <div className="absolute inset-0 flex items-center justify-center">
                <CarIcon />
            </div>
        </div>
    </div>
);

const VoiturePage = () => {
    const { t, i18n } = useTranslation();
    const [voitures, setVoitures] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showCategory, setShowCategory] = useState(false);

    const [showAdd, setShowAdd] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [editingVoiture, setEditingVoiture] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [transactionModal, setTransactionModal] = useState(null);
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('marque');
    const [galleryData, setGalleryData] = useState(null);
    const [showTransactionCreator, setShowTransactionCreator] = useState(false); // Add this state

    const fetchVoitures = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await fetch('https://krili-loto.com/api/voitures');
            if (!res.ok) throw new Error(`HTTP ${res.status}`);
            const data = await res.json();
            setVoitures(data);
        } catch (err) {
            console.error(err);
            setError(err.message);
            setVoitures([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchVoitures();
    }, []);

    const handleVoitureCreated = () => {
        fetchVoitures();
        setShowAdd(false);
    };
    const handleTransactionCreated = (newTransaction) => {

        setShowTransactionCreator(false);
        // You can add a success notification here if needed
    };

    const handleVoitureUpdated = () => {
        fetchVoitures();
        setEditingVoiture(null);
    };

    const handleDeleteVoiture = async (id) => {
        try {
            const response = await fetch(`https://krili-loto.com/api/voitures/${id}`, {
                method: 'DELETE'
            });
            if (!response.ok) throw new Error(t('delete_error', 'Échec de la suppression'));
            await fetchVoitures();
            setDeleteConfirm(null);
        } catch (error) {
            console.error("Erreur lors de la suppression:", error);
            setError(error.message);
        }
    };

    const handleViewPhotos = async (voiture) => {
        try {
            // Fetch additional photos for this vehicle
            const response = await fetch(`https://krili-loto.com/api/voitures-photos/voiture/${voiture.id}`);
            if (response.ok) {
                const additionalPhotos = await response.json();
                setGalleryData({
                    mainPhoto: voiture.photo_vehicule,
                    carteGrisePhoto: voiture.photo_carte_grise,
                    assurancePhoto: voiture.photo_assurance,
                    additionalPhotos: additionalPhotos
                });
            } else {
                // Fallback if photos endpoint fails
                setGalleryData({
                    mainPhoto: voiture.photo_vehicule,
                    carteGrisePhoto: voiture.photo_carte_grise,
                    assurancePhoto: voiture.photo_assurance,
                    additionalPhotos: []
                });
            }
        } catch (error) {
            console.error("Error fetching photos:", error);
            // Fallback on error
            setGalleryData({
                mainPhoto: voiture.photo_vehicule,
                carteGrisePhoto: voiture.photo_carte_grise,
                assurancePhoto: voiture.photo_assurance,
                additionalPhotos: []
            });
        }
    };

    const filteredAndSortedVoitures = voitures
        .filter(voiture => {
            const matchesSearch = (
                voiture.marque?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                voiture.modele?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                voiture.matricule?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                voiture.numero_chassis?.toLowerCase().includes(searchTerm.toLowerCase())
            );

            const matchesStatus = filterStatus === 'all' ||
                (filterStatus === 'available' && voiture.disponible) ||
                (filterStatus === 'unavailable' && !voiture.disponible);

            return matchesSearch && matchesStatus;
        })
        .sort((a, b) => {
            switch (sortBy) {
                case 'prix':
                    return (a.prix_location_jour || 0) - (b.prix_location_jour || 0);
                case 'annee':
                    return (b.annee || 0) - (a.annee || 0);
                case 'marque':
                default:
                    return (a.marque || '').localeCompare(b.marque || '');
            }
        });

    const getStatusCounts = () => {
        const total = voitures.length;
        const available = voitures.filter(v => v.disponible).length;
        const unavailable = total - available;
        return { total, available, unavailable };
    };

    const statusCounts = getStatusCounts();

    return (
        <Sidebar>
            <div className={`min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-6 ${showAdd || editingVoiture ? 'overflow-hidden' : ''} ${i18n.language === 'ar' ? 'rtl' : ''}`}>
                <div className="max-w-7xl mx-auto relative">
                    {/* Main content with blur effect when modal is open */}
                    <div
                        className={`transition-all duration-300 ${showAdd || editingVoiture || showTransactionCreator ? 'blur-sm' : ''}`}>
                        {/* Header Section */}
                        <div
                            className="bg-white/90 backdrop-blur-lg rounded-3xl shadow-2xl p-8 mb-8 border border-white/40">
                            <div
                                className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-8">
                                {/* Header Section */}
                                <div className="flex-1">
                                    <div className="flex items-center mb-4">
                                        <div
                                            className={`p-3 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl shadow-lg ${i18n.language === 'ar' ? 'ml-4' : 'mr-4'}`}>
                                            <CarIcon className="text-white text-xl" />
                                        </div>
                                        <div>
                                            <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                                                {t('manage_vehicles')}
                                            </h1>
                                            <p className="text-gray-500 text-sm mt-1">{t('fleet_mgmt_desc')}</p>
                                        </div>
                                    </div>

                                    {/* Status Cards */}
                                    <div className="flex flex-wrap gap-6">
                                        <div
                                            className="bg-gradient-to-r from-blue-50 to-indigo-50 px-4 py-3 rounded-2xl border border-blue-100 shadow-sm">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-blue-500 rounded-full shadow-sm"></div>
                                                <span
                                                    className="font-semibold text-gray-700">{t('total')}: {statusCounts.total}</span>
                                            </div>
                                        </div>
                                        <div
                                            className="bg-gradient-to-r from-green-50 to-emerald-50 px-4 py-3 rounded-2xl border border-green-100 shadow-sm">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-green-500 rounded-full shadow-sm"></div>
                                                <span
                                                    className="font-semibold text-gray-700">{t('available')}: {statusCounts.available}</span>
                                            </div>
                                        </div>
                                        <div
                                            className="bg-gradient-to-r from-red-50 to-rose-50 px-4 py-3 rounded-2xl border border-red-100 shadow-sm">
                                            <div className="flex items-center space-x-3">
                                                <div className="w-3 h-3 bg-red-500 rounded-full shadow-sm"></div>
                                                <span
                                                    className="font-semibold text-gray-700">{t('unavailable')}: {statusCounts.unavailable}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Action Buttons */}
                                <div className="flex flex-col sm:flex-row lg:flex-col xl:flex-row gap-4">

                                    <button
                                        onClick={() => setShowTransactionCreator(true)}
                                        className="flex items-center space-x-3 bg-gradient-to-r from-green-500 to-emerald-600 text-white px-6 py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 group border border-green-400/30"
                                    >
                                        <div className="p-1 bg-white/20 rounded-lg">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                        </div>
                                        <span className="font-semibold text-sm">{t('new_transaction')}</span>
                                    </button>
                                    <button
                                        onClick={() => setShowCategory(true)}
                                        className="flex items-center space-x-3 bg-gradient-to-r from-indigo-500 to-purple-600 text-white px-6 py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 group border border-indigo-400/30"
                                    >
                                        <div className="p-1 bg-white/20 rounded-lg">
                                            <svg className="w-4 h-4" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M3 12h18M12 3v18" />
                                            </svg>
                                        </div>
                                        <span className="font-semibold text-sm">{t('manage_categories')}</span>
                                    </button>


                                    <button
                                        onClick={() => {
                                            setEditingVoiture(null);
                                            setShowAdd(true);
                                        }}
                                        className="flex items-center space-x-3 bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-3.5 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 active:scale-95 group border border-blue-400/30"
                                    >
                                        <div
                                            className="p-1 bg-white/20 rounded-lg group-hover:rotate-90 transition-transform duration-300">
                                            <AddIcon className="w-4 h-4" />
                                        </div>
                                        <span className="font-semibold text-sm">{t('new_vehicle')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>


                        {showCategory && (
                            <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                                <div
                                    className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                                    onClick={() => setShowCategory(false)}
                                ></div>

                                <div
                                    className="relative z-10 w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white rounded-2xl shadow-2xl p-6">
                                    <div className="flex justify-between items-center mb-4">
                                        <h2 className="text-2xl font-bold text-gray-800">{t('manage_categories')}</h2>
                                        <button
                                            onClick={() => setShowCategory(false)}
                                            className="text-gray-500 hover:text-gray-700"
                                        >
                                            <svg className="w-6 h-6" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                    <CategoryManager />
                                </div>
                            </div>
                        )}


                        {/* Search and Filter Section */}
                        <div
                            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl p-6 mb-6 border border-white/20">
                            <div className="flex flex-col lg:flex-row gap-4">
                                {/* Search bar */}
                                <div className="relative flex-grow">
                                    <div
                                        className={`absolute inset-y-0 ${i18n.language === 'ar' ? 'right-0 pr-4' : 'left-0 pl-4'} flex items-center pointer-events-none`}>
                                        <svg className="h-5 w-5 text-blue-400" fill="none" stroke="currentColor"
                                            viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                    </div>
                                    <input
                                        type="text"
                                        placeholder={t('search_vehicles_placeholder')}
                                        className={`block w-full ${i18n.language === 'ar' ? 'pr-12 pl-12' : 'pl-12 pr-12'} py-3 border-2 border-blue-100 rounded-xl bg-white/90 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 text-gray-700 placeholder-blue-300 transition-all duration-300 hover:shadow-md`}
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                    />
                                    {searchTerm && (
                                        <button
                                            onClick={() => setSearchTerm('')}
                                            className={`absolute inset-y-0 ${i18n.language === 'ar' ? 'left-0 pl-4' : 'right-0 pr-4'} flex items-center text-blue-400 hover:text-blue-600 transition-colors`}
                                        >
                                            <CloseIcon />
                                        </button>
                                    )}
                                </div>

                                {/* Filter controls */}
                                <div className="flex flex-wrap gap-3">
                                    <div className="flex items-center space-x-2">
                                        <FilterIcon className="w-4 h-4 text-gray-500" />
                                        <select
                                            value={filterStatus}
                                            onChange={(e) => setFilterStatus(e.target.value)}
                                            className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all"
                                        >
                                            <option value="all">{t('all_status')}</option>
                                            <option value="available">{t('available')}</option>
                                            <option value="unavailable">{t('unavailable')}</option>
                                        </select>
                                    </div>

                                    <select
                                        value={sortBy}
                                        onChange={(e) => setSortBy(e.target.value)}
                                        className="px-4 py-2 bg-white border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-blue-400 transition-all"
                                    >
                                        <option value="marque">{t('sort_by_brand')}</option>
                                        <option value="prix">{t('sort_by_price')}</option>
                                        <option value="annee">{t('sort_by_year')}</option>
                                    </select>
                                </div>
                            </div>
                        </div>

                        {/* Results Section */}
                        <div
                            className="bg-white/80 backdrop-blur-sm rounded-2xl shadow-xl overflow-hidden border border-white/20">
                            <div className="p-6">
                                <div className="flex justify-between items-center mb-6">
                                    <h2 className="text-xl font-semibold text-gray-800">
                                        {t('search_results')}
                                    </h2>
                                    <div className="text-sm text-gray-500 bg-blue-50 px-3 py-1 rounded-full">
                                        {filteredAndSortedVoitures.length} {filteredAndSortedVoitures.length === 1 ? t('vehicle_found') : t('vehicles_found')}
                                    </div>
                                </div>

                                {error && (
                                    <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                                        <div className="flex items-center">
                                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                            </svg>
                                            <span className="text-red-700 font-medium">{t('error')}: {error}</span>
                                        </div>
                                    </div>
                                )}

                                {loading ? (
                                    <LoadingSpinner />
                                ) : voitures.length === 0 ? (
                                    <div className="text-center py-12">
                                        <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                                                d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                        </svg>
                                        <h3 className="text-xl font-medium text-gray-700 mb-2">{t('no_vehicle_found')}</h3>
                                        <p className="text-gray-500 mb-6">{t('add_first_vehicle_desc')}</p>
                                        <button
                                            onClick={() => setShowAdd(true)}
                                            className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:scale-105"
                                        >
                                            <AddIcon className={`${i18n.language === 'ar' ? 'ml-2' : 'mr-2'}`} />
                                            {t('add_vehicle')}
                                        </button>
                                    </div>
                                ) : filteredAndSortedVoitures.length === 0 ? (
                                    <div className="text-center py-12">
                                        <svg className="mx-auto h-16 w-16 text-gray-400 mb-4" fill="none"
                                            stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1}
                                                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                        </svg>
                                        <h3 className="text-xl font-medium text-gray-700 mb-2">{t('no_results')}</h3>
                                        <p className="text-gray-500 mb-4">{t('no_matching_vehicles')}</p>
                                        <button
                                            onClick={() => {
                                                setSearchTerm('');
                                                setFilterStatus('all');
                                            }}
                                            className="text-blue-600 hover:text-blue-700 font-medium transition-colors"
                                        >
                                            {t('reset_filters')}
                                        </button>
                                    </div>
                                ) : (
                                    <div
                                        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-3 gap-6">
                                        {filteredAndSortedVoitures.map(voiture => (
                                            <VoitureCard
                                                key={voiture.id}
                                                voiture={voiture}
                                                onEdit={setEditingVoiture}
                                                onDelete={setDeleteConfirm}
                                                onViewPhotos={() => handleViewPhotos(voiture)}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                    {showTransactionCreator && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                            <div
                                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                                onClick={() => setShowTransactionCreator(false)}
                            ></div>

                            <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                                <QuickTransactionCreator
                                    onSuccess={handleTransactionCreated}
                                    onCancel={() => setShowTransactionCreator(false)}
                                />
                            </div>
                        </div>
                    )}

                    {/* Add/Edit Voiture Modal */}
                    {(showAdd || editingVoiture) && (
                        <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
                            <div
                                className="absolute inset-0 bg-black/50 backdrop-blur-sm"
                                onClick={() => {
                                    setShowAdd(false);
                                    setEditingVoiture(null);
                                }}
                            ></div>

                            <div className="relative z-10 w-full max-w-5xl rounded-2xl">
                                <AddVoiture
                                    onCreated={handleVoitureCreated}
                                    onUpdated={handleVoitureUpdated}
                                    editingVoiture={editingVoiture}
                                    onCancel={() => {
                                        setShowAdd(false);
                                        setEditingVoiture(null);
                                    }}
                                />
                            </div>
                        </div>
                    )}

                    {/* Delete confirmation modal */}
                    {deleteConfirm && (
                        <div
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                            <div
                                className="bg-white rounded-2xl shadow-2xl overflow-hidden w-full max-w-md border border-gray-200 animate-fadeIn">
                                <div className="p-6">
                                    <div className="flex items-start">
                                        <div
                                            className={`flex-shrink-0 h-12 w-12 rounded-full bg-red-100 flex items-center justify-center ${i18n.language === 'ar' ? 'ml-4' : 'mr-4'}`}>
                                            <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor"
                                                viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                                                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                            </svg>
                                        </div>
                                        <div className={i18n.language === 'ar' ? 'mr-4 text-right' : 'ml-4 text-left'}>
                                            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('delete_vehicle_title')}</h3>
                                            <p className="text-sm text-gray-600">
                                                {t('delete_vehicle_confirm')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                                <div className={`bg-gray-50 px-6 py-4 flex justify-end ${i18n.language === 'ar' ? 'flex-row-reverse space-x-reverse' : 'space-x-3'}`}>
                                    <button
                                        onClick={() => setDeleteConfirm(null)}
                                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
                                    >
                                        {t('cancel')}
                                    </button>
                                    <button
                                        onClick={() => handleDeleteVoiture(deleteConfirm)}
                                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors flex items-center space-x-2 font-medium"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                        </svg>
                                        <span>{t('delete')}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Gallery Modal */}
                    {galleryData && (
                        <VoitureGalleryModal
                            {...galleryData}
                            onClose={() => setGalleryData(null)}
                        />
                    )}
                </div>
            </div>
            <style>{`
                .rtl { direction: rtl; }
                .rtl input, .rtl select { text-align: right; }
            `}</style>
        </Sidebar>
    );
};

export default VoiturePage;
